import { useState } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { track } from "@crm/analytics";

export function CryptoCheckoutForm({
    selectedPlan,
    isYearly,
    paymentDetails,
    onSuccess,
    onCancel,
    t,
}) {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [paymentElementComplete, setPaymentElementComplete] = useState(false);

    // Calculate price based on billing cycle
    const price = paymentDetails?.amount || (isYearly
        ? parseFloat(selectedPlan.priceYearly) * 12
        : parseFloat(selectedPlan.price));
    const credits = paymentDetails?.credits || (isYearly
        ? parseInt(selectedPlan.credits) * 12
        : parseInt(selectedPlan.credits));

    // Calculate expiry date based on billing cycle
    const expiryDate = new Date();
    if (isYearly) {
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    } else {
        expiryDate.setMonth(expiryDate.getMonth() + 1);
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            // Submit the PaymentElement to validate and collect payment details
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setError(submitError.message);
                setIsProcessing(false);
                return;
            }

            // Confirm the PaymentIntent
            const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/billing?crypto_success=true`,
                },
                redirect: "if_required",
            });

            if (confirmError) {
                setError(confirmError.message || "Payment failed. Please try again.");
                setIsProcessing(false);
                return;
            }

            if (paymentIntent?.status === "succeeded") {
                track("crypto_subscription_completed", {
                    plan: selectedPlan.name,
                    amount: price,
                    billingCycle: isYearly ? "annual" : "monthly",
                });
                onSuccess();
            } else if (paymentIntent?.status === "processing") {
                // Payment is processing - this is normal for bank transfers
                track("crypto_subscription_processing", {
                    plan: selectedPlan.name,
                    amount: price,
                    billingCycle: isYearly ? "annual" : "monthly",
                });
                // Show success anyway - webhook will handle completion
                onSuccess();
            } else {
                setError(`Payment status: ${paymentIntent?.status}. Please try again.`);
            }
        } catch (err) {
            console.error("Error processing crypto payment:", err);
            setError(err.message || "An unexpected error occurred");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Order Summary */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">
                            {paymentDetails?.planName || selectedPlan.name}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                            {isYearly
                                ? (t("billing.cryptoCheckout.annualSubscription") || "Annual Subscription (Crypto)")
                                : (t("billing.cryptoCheckout.monthlySubscription") || "Monthly Subscription (Crypto)")}
                        </p>
                    </div>
                    <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                        <span>USDC</span>
                    </div>
                </div>

                {/* Credits Info */}
                <div className="bg-white/60 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                            {t("billing.cryptoCheckout.creditsIncluded") || "Credits Included"}
                        </span>
                        <span className="text-lg font-bold text-orange-600">
                            {credits.toLocaleString()}
                        </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        {isYearly
                            ? (t("billing.cryptoCheckout.creditsDescriptionAnnual") || "12 months of credits, loaded upfront")
                            : (t("billing.cryptoCheckout.creditsDescriptionMonthly") || "1 month of credits")}
                    </p>
                </div>

                {/* Subscription Period */}
                <div className="bg-white/60 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                            {t("billing.cryptoCheckout.subscriptionEnds") || "Subscription Ends"}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                            {expiryDate.toLocaleDateString()}
                        </span>
                    </div>
                </div>

                <div className="flex items-baseline justify-between pt-4 border-t border-orange-200">
                    <span className="text-gray-700 font-medium">
                        {t("billing.totalToday") || "Total today"}
                    </span>
                    <div className="text-right">
                        <span className="text-3xl font-bold text-gray-900">
                            ${price.toFixed(2)}
                        </span>
                        <span className="text-gray-600 ml-1">{isYearly ? "/year" : "/month"}</span>
                    </div>
                </div>
            </div>

            {/* Payment Method */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {t("billing.paymentMethod") || "Payment Method"}
                </label>
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <PaymentElement
                        options={{
                            layout: "accordion",
                            defaultCollapsed: false,
                        }}
                        onChange={(e) => setPaymentElementComplete(e.complete)}
                    />
                </div>
                <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                    <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                    </svg>
                    {t("billing.cryptoCheckout.securePayment") ||
                        "Secure payment powered by Stripe"}
                </p>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-start">
                        <svg
                            className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <div className="ml-3 flex-1">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setError(null)}
                            className="ml-3 flex-shrink-0 text-red-400 hover:text-red-600"
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
                <button
                    type="submit"
                    disabled={!stripe || isProcessing || !paymentElementComplete}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl"
                >
                    {isProcessing ? (
                        <span className="flex items-center justify-center gap-2">
                            <svg
                                className="animate-spin h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            {t("billing.cryptoCheckout.processing") ||
                                "Processing crypto payment..."}
                        </span>
                    ) : (
                        <span className="flex items-center justify-center gap-2">
                            <span className="text-lg">$</span>
                            {t("billing.cryptoCheckout.payNow") ||
                                `Pay $${price.toFixed(2)} with Crypto`}
                        </span>
                    )}
                </button>

                {/* Trust Signals */}
                <div className="mt-4 flex items-center justify-center gap-6 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                        <svg
                            className="w-4 h-4 text-orange-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <span>
                            {t("billing.cryptoCheckout.oneTimePayment") || "One-time payment"}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <svg
                            className="w-4 h-4 text-orange-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <span>
                            {t("billing.cryptoCheckout.stablecoinAccepted") || "USDC accepted"}
                        </span>
                    </div>
                </div>

                {/* No Auto-Renewal Reminder */}
                <p className="mt-4 text-xs text-center text-gray-500">
                    {t("billing.cryptoCheckout.renewalReminder") ||
                        "You will receive a reminder 30 days before your subscription expires."}
                </p>
            </div>
        </form>
    );
}
